import { useI18n } from 'vue-i18n';
import { FORM_VALIDATIONS } from '../constants';
import type { FieldSchema } from '../types';
import { emailValidation, microchipValidation, phoneValidation } from '../helpers';

export default function () {
  const { t } = useI18n();
  // CASES
  function emailFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validEmail';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = emailValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function microchipFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.requiredMicrochipLength';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = microchipValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function amountFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.amount';
    const ERROR_MESSAGE_MISSING_UNIT = 'components.forms.errors.amountMissingUnit';

    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const { amount, amount_unit } = model;

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);

    if ((amount && !amount_unit) || (amount_unit && !amount)) {
      state.errors.add(ERROR_MESSAGE_MISSING_UNIT);
      model.value = undefined;
      return;
    }

    if (!amount) {
      model.value = undefined;
      state.errors.delete(ERROR_MESSAGE);
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
      return;
    }

    if (amount_unit) {
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
    }

    if (typeof amount !== 'string' || amount !== amount.trim()) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }

    // NOTE: This portion of the code was done by Gerald.
    const doseRegex = /^((\d{0,4})?([.,]\d{1,4})?|\d{1,4} \d{1,4}\/\d{1,4}|\d{1,4}\/\d{1,4})$/;
    if (!doseRegex.test(amount)) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }
    try {
      if (amount.includes('/')) {
        const parts = amount.split(' ');
        let whole = 0,
          numerator = 0,
          denominator = 1;
        if (parts.length === 2) {
          whole = parseInt(parts[0], 10);
          [numerator, denominator] = parts[1].split('/').map(Number);
        } else {
          [numerator, denominator] = parts[0].split('/').map(Number);
        }
        if (
          denominator === 0 ||
          whole.toString().length > 4 ||
          numerator.toString().length > 4 ||
          denominator.toString().length > 4
        ) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.includes('.') || amount.includes(',')) {
        const normalized = amount.replace(',', '.');
        const [intPart, decimalPart] = normalized.split('.');
        if (intPart.length > 4 || decimalPart.length > 4) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.length > 4) {
        state.errors.add(ERROR_MESSAGE);
        model.value = undefined;
        return;
      }
      state.errors.delete(ERROR_MESSAGE);
    } catch (e) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
    }

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
  }

  function phoneFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validPhone';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = phoneValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function emailTypeFieldValidation(field: FieldSchema) {
    const EMPTY_EMAIL = 'components.forms.errors.onePrimaryEmailRequired';
    const { state, model } = field;

    if (!model) return state.errors.delete(EMPTY_EMAIL);

    const hasPrimary = field.model.filter((entry: any) => entry.primary).length;
    if (hasPrimary) return state.errors.delete(EMPTY_EMAIL);

    state.errors.add(EMPTY_EMAIL);
  }

  function contactTypeFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE =
      field.name === 'emails'
        ? 'components.forms.errors.validEmailReview'
        : 'components.forms.errors.validPhoneReview';

    const { state, model } = field;

    const hasError = model?.some((entry: any) => entry.error);

    if (hasError) return state.errors.add(ERROR_MESSAGE);

    state.errors.delete(ERROR_MESSAGE);
  }

  // CUSTOM VALIDATIONS
  function minLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.minLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length >= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function maxLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.maxLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length <= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  // VALIDATION TYPES
  function fieldTypeValidation(field: FieldSchema) {
    const { type } = field.settings;

    const needsValidation = [
      'contact',
      'contact_type',
      'email',
      'email_type',
      'microchip',
      'tel',
      'amount_unit',
    ];
    if (!type || !needsValidation.includes(type)) return;

    //prettier-ignore
    switch (type) {
      case 'email': return emailFieldValidation(field);
      case 'contact': return field.name === 'email' ? emailFieldValidation(field) : phoneFieldValidation(field);
      case 'contact_type': return contactTypeFieldValidation(field)
      case 'tel': return phoneFieldValidation(field);
      case 'email_type': return emailTypeFieldValidation(field);
      case 'microchip': return microchipFieldValidation(field);
      case 'amount_unit': return amountFieldValidation(field);
    }
  }

  function fieldCustomValidation(field: FieldSchema) {
    if (!field.settings?.validations) return;

    const { validations } = field.settings;
    const { MAX_LENGTH, MIN_LENGTH } = FORM_VALIDATIONS;

    validations?.forEach(({ validation, rule }) => {
      //prettier-ignore
      switch (validation) {
        case MIN_LENGTH: return minLengthValidation(field, rule);
        case MAX_LENGTH: maxLengthValidation(field, rule);
      }
    });
  }

  function _requiredField(field: any) {
    const REQUIRED_ERROR_MESSAGE = 'components.forms.errors.required';
    const { model, settings, state } = field;
    const { required } = settings;

    const modelNotEmpty: boolean = Array.isArray(model) ? !!model.length : !!model;

    if (required && !modelNotEmpty) return state.errors.add(REQUIRED_ERROR_MESSAGE);

    state.errors.delete(REQUIRED_ERROR_MESSAGE);
  }

  // HANDLER
  function validateField(field: FieldSchema, wasFormSubmitted: boolean = false) {
    if (field.settings.required && wasFormSubmitted) _requiredField(field);

    fieldTypeValidation(field);
    fieldCustomValidation(field);
  }

  return {
    emailFieldValidation,
    phoneFieldValidation,
    validateField,
  };
}
